import { useEffect, useRef, useState, type FormEvent } from "react";
import { LiveCallHandling } from "./LiveCallHandling";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Settings = {
  threeWay: boolean;
  callWaiting: boolean;
  doNotDisturb: boolean;
  voicemailEnabled: boolean;
  ringSeconds: number;
  forwardMode: "off" | "always" | "busy" | "unanswered";
  forwardTo: string;
  incomingCallerId: boolean;
  outgoingCallerId: "show" | "private";
  callerIdNumberId: string | null;
  callerIdName: string;
};
type Line = {
  id: string;
  label: string;
  extension: string | null;
  device: string | null;
  port: number | null;
  outgoingNumber: string | null;
  version: number;
  settings: Partial<Settings> | null;
  applicationStatus: string;
};
type State = { items: Line[]; numbers: { id: string; e164: string }[] };
const defaults: Settings = {
  threeWay: true,
  callWaiting: true,
  doNotDisturb: false,
  voicemailEnabled: true,
  ringSeconds: 25,
  forwardMode: "off",
  forwardTo: "",
  incomingCallerId: true,
  outgoingCallerId: "show",
  callerIdNumberId: null,
  callerIdName: "",
};

export function FeatureForm({
  line,
  numbers,
  readOnly,
  saving,
  onSave,
}: {
  line: Line;
  numbers: State["numbers"];
  readOnly: boolean;
  saving: boolean;
  onSave: (settings: Settings) => Promise<void>;
}) {
  const initial = { ...defaults, ...line.settings };
  const [forward, setForward] = useState(initial.forwardMode);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving || readOnly) return;
    const form = new FormData(event.currentTarget);
    await onSave({
      threeWay: form.get("threeWay") === "on",
      callWaiting: form.get("callWaiting") === "on",
      doNotDisturb: form.get("doNotDisturb") === "on",
      voicemailEnabled: form.get("voicemailEnabled") === "on",
      ringSeconds: Number(form.get("ringSeconds")),
      forwardMode: forward,
      forwardTo: forward === "off" ? "" : String(form.get("forwardTo") ?? ""),
      incomingCallerId: form.get("incomingCallerId") === "on",
      outgoingCallerId: String(
        form.get("outgoingCallerId"),
      ) as Settings["outgoingCallerId"],
      callerIdNumberId: String(form.get("callerIdNumberId") ?? "") || null,
      callerIdName: String(form.get("callerIdName") ?? ""),
    });
  };
  return (
    <form className="cr-form" onSubmit={submit}>
      <p>
        <strong>{line.label}</strong>
        {line.extension ? ` · Extension ${line.extension}` : ""}
        <br />
        {line.device
          ? `${line.device}${line.port ? ` · Port ${line.port}` : ""}`
          : "Equipment not assigned"}
        {line.outgoingNumber
          ? ` · Current assigned outgoing number: ${line.outgoingNumber}`
          : ""}
      </p>
      <p className="cr-notice">
        {line.settings
          ? "These are your saved requests. The call handling section shows what is applied on the phone server. Other features still require activation."
          : "No feature request saved. The selections below are suggested settings, not readings from your phone."}
      </p>
      <fieldset className="cr-extension-fields" disabled={saving || readOnly}>
        <legend>Calling features</legend>
        <label className="cr-check">
          <input
            type="checkbox"
            name="threeWay"
            defaultChecked={initial.threeWay}
          />
          3-way calling
        </label>
        <small>
          Talk with two people on the same call. The phone or adapter must
          support conferencing.
        </small>
        <label className="cr-check">
          <input
            type="checkbox"
            name="callWaiting"
            defaultChecked={initial.callWaiting}
          />
          Call waiting
        </label>
        <small>
          Hear a tone when another call arrives while you are on the phone.
        </small>
        <label className="cr-check">
          <input
            type="checkbox"
            name="doNotDisturb"
            defaultChecked={initial.doNotDisturb}
          />
          Do Not Disturb
        </label>
        <small>Request that incoming calls do not ring this line.</small>
        <label className="cr-check">
          <input
            type="checkbox"
            name="voicemailEnabled"
            defaultChecked={initial.voicemailEnabled}
          />
          Voicemail
        </label>
        <label className="cr-field">
          <span>Ring time before unanswered handling (seconds)</span>
          <input
            name="ringSeconds"
            type="number"
            min={5}
            max={120}
            required
            defaultValue={initial.ringSeconds}
          />
        </label>
      </fieldset>
      <fieldset className="cr-extension-fields" disabled={saving || readOnly}>
        <legend>Call forwarding</legend>
        <label className="cr-field">
          <span>Forward calls</span>
          <select
            value={forward}
            onChange={(e) =>
              setForward(e.target.value as Settings["forwardMode"])
            }
          >
            <option value="off">Off</option>
            <option value="always">All calls</option>
            <option value="busy">When busy</option>
            <option value="unanswered">When unanswered</option>
          </select>
        </label>
        {forward !== "off" && (
          <label className="cr-field">
            <span>Forward to</span>
            <input
              type="tel"
              name="forwardTo"
              required
              maxLength={32}
              placeholder="Ten-digit US or Canadian number"
              defaultValue={initial.forwardTo}
            />
            <small>
              Use a different number from this line's incoming number.
            </small>
          </label>
        )}
      </fieldset>
      <fieldset className="cr-extension-fields" disabled={saving || readOnly}>
        <legend>Caller ID</legend>
        <label className="cr-check">
          <input
            type="checkbox"
            name="incomingCallerId"
            defaultChecked={initial.incomingCallerId}
          />
          Show incoming Caller ID on my phone
        </label>
        <small>
          Requires a Caller ID capable phone. Caller names depend on available
          information.
        </small>
        <label className="cr-field">
          <span>Outgoing Caller ID</span>
          <select
            name="outgoingCallerId"
            defaultValue={initial.outgoingCallerId}
          >
            <option value="show">Show my number</option>
            <option value="private">Request private number</option>
          </select>
        </label>
        <label className="cr-field">
          <span>Number shown on outgoing calls</span>
          <select
            name="callerIdNumberId"
            defaultValue={initial.callerIdNumberId ?? ""}
          >
            <option value="">Keep the line's assigned number</option>
            {initial.callerIdNumberId &&
              !numbers.some((n) => n.id === initial.callerIdNumberId) && (
                <option value={initial.callerIdNumberId} disabled>
                  Previously selected number is no longer available
                </option>
              )}
            {numbers.map((number) => (
              <option key={number.id} value={number.id}>
                {number.e164}
              </option>
            ))}
          </select>
        </label>
        <label className="cr-field">
          <span>Outgoing Caller ID name</span>
          <input
            name="callerIdName"
            maxLength={15}
            defaultValue={initial.callerIdName}
          />
          <small>
            Optional, up to 15 characters. Carrier approval and the receiving
            network determine whether the name appears. Blank keeps the existing
            name.
          </small>
        </label>
        <p>
          Emergency callback settings are managed separately and are not changed
          here.
        </p>
      </fieldset>
      {readOnly ? (
        <p>Customer view is read-only.</p>
      ) : (
        <button className="cr-button" disabled={saving}>
          {saving ? "Saving…" : "Save feature request"}
        </button>
      )}
    </form>
  );
}

export function FeaturesPanel({
  api,
  accountId,
  customer,
  csrf,
  readOnly = false,
}: {
  api: Api;
  accountId: string;
  customer: boolean;
  csrf: string | undefined;
  readOnly?: boolean;
}) {
  const [state, setState] = useState<State | null>(null),
    [selected, setSelected] = useState("");
  const [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [saving, setSaving] = useState(false),
    [refresh, setRefresh] = useState(0);
  const busy = useRef(false),
    command = useRef({ fingerprint: "", key: "" });
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/features`;
  useEffect(() => {
    const abort = new AbortController();
    setState(null);
    setError("");
    void api<State>(base, { signal: abort.signal })
      .then((value) => {
        if (abort.signal.aborted) return;
        setState(value);
        setSelected((prior) =>
          value.items.some((item) => item.id === prior)
            ? prior
            : (value.items[0]?.id ?? ""),
        );
      })
      .catch((cause) => {
        if (!abort.signal.aborted) setError(cause.message);
      });
    return () => abort.abort();
  }, [api, base, refresh]);
  const line = state?.items.find((item) => item.id === selected);
  return (
    <section className="cr-panel">
      <h2>Features</h2>
      <p>
        Choose a line, save its settings, then apply call handling to the phone
        server. Check the applied settings below before making a test call.
      </p>
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <div className="cr-notice error" role="alert">
          {error}{" "}
          <button
            className="cr-button secondary"
            disabled={saving}
            onClick={() => setRefresh((value) => value + 1)}
          >
            Refresh features
          </button>
        </div>
      )}
      {!state && !error && (
        <p role="status">Loading your lines and feature requests…</p>
      )}
      {state && !state.items.length && (
        <p>
          No telephone line has been added to this account. Contact Cache River
          to add service before requesting calling features.
        </p>
      )}
      {state && state.items.length > 0 && (
        <label className="cr-field">
          <span>Phone or line</span>
          <select
            value={selected}
            disabled={saving}
            onChange={(e) => {
              setSelected(e.target.value);
              setNotice("");
              setError("");
            }}
          >
            {state.items.map((item) => (
              <option key={item.id} value={item.id}>
                {[
                  item.label,
                  item.extension ? `Ext. ${item.extension}` : "",
                  item.outgoingNumber,
                  item.device,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </option>
            ))}
          </select>
        </label>
      )}
      {line && state && (
        <LiveCallHandling
          key={`${accountId}:${line.id}`}
          api={api}
          accountId={accountId}
          customer={customer}
          csrf={csrf}
          lineId={line.id}
          savedVersion={line.version}
          saved={line.settings}
          readOnly={readOnly || saving}
        />
      )}
      {line && state && (
        <FeatureForm
          key={`${accountId}:${line.id}:${line.version}`}
          line={line}
          numbers={state.numbers}
          readOnly={readOnly}
          saving={saving}
          onSave={async (settings) => {
            if (busy.current || readOnly) return;
            busy.current = true;
            setSaving(true);
            setError("");
            setNotice("");
            const body = {
              lineId: line.id,
              expectedVersion: line.version,
              settings,
            };
            const fingerprint = JSON.stringify(body);
            if (command.current.fingerprint !== fingerprint)
              command.current = { fingerprint, key: crypto.randomUUID() };
            try {
              await api(base, {
                method: "POST",
                csrf,
                body: { ...body, requestKey: command.current.key },
              });
              setNotice(
                "Feature request saved. Pending activation; your live phone settings have not changed.",
              );
              command.current = { fingerprint: "", key: "" };
              setRefresh((value) => value + 1);
            } catch (cause) {
              setError((cause as Error).message);
            } finally {
              busy.current = false;
              setSaving(false);
            }
          }}
        />
      )}
    </section>
  );
}
